import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { poApi } from '../../utils/purchasingApi';
import { vendorsApi } from '../../utils/vendorsApi';
import { api } from '../../utils/api';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './Purchasing.css';

const fmtNum = (n) => n != null
  ? Number(n).toLocaleString('en-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  : '0.00';

const emptyLine = (n) => ({
  lineNumber: n, prLineId: null, description: '', quantity: 1,
  unit: '', unitPrice: '', accountId: '', notes: '',
});

export default function PurchaseOrderFormPage() {
  const { id }     = useParams();
  const location   = useLocation();
  const navigate   = useNavigate();
  const isEdit     = !!id;

  // State from PR navigation
  const prState = location.state ?? {};

  const [form, setForm] = useState({
    vendorId:         '',
    date:             new Date().toISOString().slice(0, 10),
    expectedDelivery: '',
    entityId:         '',
    currencyId:       '',
    notes:            '',
  });
  const [lines, setLines] = useState([emptyLine(1)]);
  const [prId, setPrId]   = useState(prState.prId ?? null);

  // Reference data
  const [vendors,    setVendors]    = useState([]);
  const [entities,   setEntities]   = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [accounts,   setAccounts]   = useState([]);

  const [saving, setSaving]       = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [errors, setErrors]       = useState({});

  // Load reference data
  useEffect(() => {
    vendorsApi.list({ limit: 500, isActive: true })
      .then((r) => setVendors(r?.items ?? []))
      .catch(() => {});
    api.get('/entities')
      .then((r) => setEntities(r?.items ?? r ?? []))
      .catch(() => {});
    api.get('/currencies')
      .then((r) => setCurrencies(r?.items ?? r ?? []))
      .catch(() => {});
    api.get('/accounts?type=POSTING&limit=1000')
      .then((r) => setAccounts(r?.items ?? r ?? []))
      .catch(() => {});
  }, []);

  // Load PO for edit mode
  useEffect(() => {
    if (!isEdit) return;
    poApi.getById(id).then((po) => {
      setForm({
        vendorId:         po.vendorId,
        date:             po.date?.slice(0, 10) ?? '',
        expectedDelivery: po.expectedDelivery?.slice(0, 10) ?? '',
        entityId:         po.entityId ?? '',
        currencyId:       po.currencyId,
        notes:            po.notes ?? '',
      });
      setLines(po.lines.map((l) => ({
        lineNumber:  l.lineNumber,
        prLineId:    l.prLineId ?? null,
        description: l.description,
        quantity:    l.quantity,
        unit:        l.unit ?? '',
        unitPrice:   l.unitPrice,
        accountId:   l.accountId ?? '',
        notes:       l.notes ?? '',
      })));
      setPrId(po.prId ?? null);
    }).catch(() => navigate('/purchasing/orders'))
      .finally(() => setPageLoading(false));
  }, [id, isEdit, navigate]);

  // Pre-populate from PR state (create from PR)
  useEffect(() => {
    if (isEdit || !prState.prLines?.length) return;
    setLines(prState.prLines.map((l, i) => ({
      lineNumber:  i + 1,
      prLineId:    l.id,
      description: l.description,
      quantity:    l.quantity,
      unit:        l.unit ?? '',
      unitPrice:   '',
      accountId:   '',
      notes:       '',
    })));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setLine = (i, k, v) =>
    setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));

  const addLine = () =>
    setLines((ls) => [...ls, emptyLine(ls.length + 1)]);

  const removeLine = (i) =>
    setLines((ls) => ls.filter((_, idx) => idx !== i).map((l, idx) => ({ ...l, lineNumber: idx + 1 })));

  const grandTotal = lines.reduce((s, l) => {
    const qty   = Number(l.quantity)  || 0;
    const price = Number(l.unitPrice) || 0;
    return s + qty * price;
  }, 0);

  const validate = () => {
    const e = {};
    if (!form.vendorId)   e.vendorId   = 'Required';
    if (!form.date)       e.date       = 'Required';
    if (!form.currencyId) e.currencyId = 'Required';
    lines.forEach((l, i) => {
      if (!l.description.trim())              e[`line_${i}_desc`]  = 'Required';
      if (!l.quantity || Number(l.quantity) <= 0) e[`line_${i}_qty`] = 'Must be > 0';
      if (!l.unitPrice || Number(l.unitPrice) < 0) e[`line_${i}_price`] = 'Required';
    });
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const payload = {
        vendorId:         form.vendorId,
        date:             form.date,
        expectedDelivery: form.expectedDelivery || undefined,
        entityId:         form.entityId  || undefined,
        currencyId:       form.currencyId,
        notes:            form.notes     || undefined,
        prId:             prId           || undefined,
        lines: lines.map((l) => ({
          lineNumber:  l.lineNumber,
          prLineId:    l.prLineId || undefined,
          description: l.description.trim(),
          quantity:    Number(l.quantity),
          unit:        l.unit.trim()  || undefined,
          unitPrice:   Number(l.unitPrice),
          accountId:   l.accountId   || undefined,
          notes:       l.notes?.trim() || undefined,
        })),
      };
      const po = isEdit
        ? await poApi.update(id, payload)
        : await poApi.create(payload);
      navigate('/purchasing/orders', { state: { toast: `PO-${po.poNumber} saved.` } });
    } catch (err) {
      setErrors({ api: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (pageLoading) return <LoadingSpinner variant="fullpage" />;

  const currencySymbol = currencies.find((c) => c.id === form.currencyId)?.symbol ?? '';

  return (
    <div className="purch-form-page">
      {/* Header */}
      <div className="purch-form-page-header">
        <button className="purch-back-btn" onClick={() => navigate('/purchasing/orders')}>
          <i className="fas fa-arrow-left" /> Purchase Orders
        </button>
        <h1 className="purch-page-title">
          {isEdit ? 'Edit Purchase Order' : 'New Purchase Order'}
        </h1>
      </div>

      {errors.api && (
        <div className="purch-error-banner" style={{ marginBottom: 16 }}>
          <i className="fas fa-exclamation-circle" /> {errors.api}
        </div>
      )}

      {/* PR link card */}
      {(prId || prState.prNumber) && (
        <div className="purch-pr-link-card">
          <i className="fas fa-link" />
          <span>Linked to PR-{prState.prNumber || prId}</span>
        </div>
      )}

      {/* PO header fields */}
      <div className="purch-form-card">
        <p className="purch-form-section-title">Order Details</p>

        <div className="purch-form-row">
          <div className="purch-field">
            <label className="purch-label">Vendor *</label>
            <select className={`purch-input${errors.vendorId ? ' purch-input--err' : ''}`}
              value={form.vendorId}
              onChange={(e) => setForm((f) => ({ ...f, vendorId: e.target.value }))}>
              <option value="">— select vendor —</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.code} — {v.name}</option>)}
            </select>
          </div>
          <div className="purch-field">
            <label className="purch-label">Currency *</label>
            <select className={`purch-input${errors.currencyId ? ' purch-input--err' : ''}`}
              value={form.currencyId}
              onChange={(e) => setForm((f) => ({ ...f, currencyId: e.target.value }))}>
              <option value="">— select currency —</option>
              {currencies.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
            </select>
          </div>
        </div>

        <div className="purch-form-row">
          <div className="purch-field">
            <label className="purch-label">Order Date *</label>
            <input type="date"
              className={`purch-input${errors.date ? ' purch-input--err' : ''}`}
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
          </div>
          <div className="purch-field">
            <label className="purch-label">Expected Delivery</label>
            <input type="date" className="purch-input"
              value={form.expectedDelivery}
              onChange={(e) => setForm((f) => ({ ...f, expectedDelivery: e.target.value }))} />
          </div>
        </div>

        <div className="purch-form-row" style={{ marginBottom: 0 }}>
          <div className="purch-field">
            <label className="purch-label">Sub-Entity</label>
            <select className="purch-input" value={form.entityId}
              onChange={(e) => setForm((f) => ({ ...f, entityId: e.target.value }))}>
              <option value="">— all entities —</option>
              {entities.map((en) => <option key={en.id} value={en.id}>{en.code} — {en.name}</option>)}
            </select>
          </div>
          <div className="purch-field">
            <label className="purch-label">Notes</label>
            <input className="purch-input" value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="purch-form-card">
        <div className="purch-lines-header">
          <p className="purch-form-section-title" style={{ margin: 0 }}>Line Items</p>
          <button className="purch-btn purch-btn--ghost purch-btn--sm" type="button" onClick={addLine}>
            <i className="fas fa-plus" /> Add Line
          </button>
        </div>

        <div className="purch-lines" style={{ marginTop: 12 }}>
          {lines.map((l, i) => (
            <div key={i} className="purch-line-row">
              <span className="purch-line-num">{l.lineNumber}</span>

              <div className="purch-field purch-field--grow">
                <input className={`purch-input${errors[`line_${i}_desc`] ? ' purch-input--err' : ''}`}
                  placeholder="Description *"
                  value={l.description}
                  onChange={(e) => setLine(i, 'description', e.target.value)} />
              </div>

              <div className="purch-field purch-field--sm">
                <input className={`purch-input${errors[`line_${i}_qty`] ? ' purch-input--err' : ''}`}
                  type="number" min="0.0001" step="any" placeholder="Qty *"
                  value={l.quantity}
                  onChange={(e) => setLine(i, 'quantity', e.target.value)} />
              </div>

              <div className="purch-field purch-field--xs">
                <input className="purch-input" placeholder="Unit"
                  value={l.unit}
                  onChange={(e) => setLine(i, 'unit', e.target.value)} />
              </div>

              <div className="purch-field purch-field--sm">
                <input className={`purch-input${errors[`line_${i}_price`] ? ' purch-input--err' : ''}`}
                  type="number" min="0" step="any" placeholder="Unit Price *"
                  value={l.unitPrice}
                  onChange={(e) => setLine(i, 'unitPrice', e.target.value)} />
              </div>

              <div className="purch-field purch-field--md" style={{ minWidth: 0 }}>
                <select className="purch-input purch-select--sm" value={l.accountId}
                  onChange={(e) => setLine(i, 'accountId', e.target.value)}>
                  <option value="">Account</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
                </select>
              </div>

              <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 80, fontSize: '0.8125rem', paddingTop: 10, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>
                {currencySymbol} {fmtNum(Number(l.quantity || 0) * Number(l.unitPrice || 0))}
              </div>

              <button className="purch-btn purch-btn--icon purch-btn--danger"
                disabled={lines.length === 1}
                onClick={() => removeLine(i)}>
                <i className="fas fa-trash" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Grand total */}
      <div className="purch-total-card">
        <span className="purch-total-label">Grand Total</span>
        <span className="purch-total-val">{currencySymbol} {fmtNum(grandTotal)}</span>
      </div>

      {/* Actions */}
      <div className="purch-form-actions">
        <button className="purch-btn purch-btn--ghost"
          onClick={() => navigate('/purchasing/orders')}
          disabled={saving}>
          Cancel
        </button>
        <button className="purch-btn purch-btn--primary" onClick={handleSave} disabled={saving}>
          {saving ? <LoadingSpinner variant="button" /> : isEdit ? 'Save Changes' : 'Save as Draft'}
        </button>
      </div>
    </div>
  );
}
